import { CouplesUsuallySpendEmptyDetails } from '@components/couples-usually-spend/CouplesUsuallySpendEmptyDetails';
import { Packages } from '@components/detailed-pricing/Packages';
import { PricingCta } from '@components/detailed-pricing/PricingCta';
import { usePdfUrl } from '@components/pricing/hooks/use-pdf-url';
import { PricingPdf } from '@components/pricing/pdf';
import { CUSSecondaryCta } from '@components/pricing/pricing-details/CUSSecondaryCta';
import { EstimatedUsuallySpend } from '@components/shared-pricing/EstimatedUsuallySpend';
import { CATEGORY_SINGULAR_ABBREVIATED_TERMS } from '@constants/categories';
import { useEstimatedPricing } from '@hooks/use-estimated-pricing';
import { useLiteStorefront } from '@hooks/use-lite-storefront';
import { useStorefrontCouplesUsuallySpend } from '@hooks/useStorefrontCouplesUsuallySpend';
import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { useAppSelector } from '@redux/hooks';
import { Icon } from '@tkww/orion-web-ds-graphic';
import { Body1, H3 } from '@xo-union/tk-ui-typography';
import { useAtomValue } from 'jotai';
import React, { type FC } from 'react';
import type { CategoryCode } from 'types/Category';
import {
	detailedPricingUsuallySpendAtom,
	hideDetailedPricingAtom,
	packagesAtom,
} from '../../jotai/storefront/detailed-pricing';
import { EmptyStateSearchLink } from '../../pages/Storefront/components/EmptyStateSearchLink/EmptyStateSearchLink';
import { RelatedSearches } from '../../pages/Storefront/containers/lite_vendor_about/components/related_searches';
import type { NavItemProps } from '../../pages/Storefront/containers/nav/types';
import Styles from './package-pricing.scss';

const PDF_TEXT =
	'See all the vendor’s pricing and services in this helpful guide.';

const NoPricingDetails = () => {
	const isLiteStorefront = useLiteStorefront();
	const category = useAppSelector(
		(state) => state.vendor.vendor?.categoryCode as CategoryCode,
	);

	if (!isLiteStorefront) {
		return (
			<EmptyStateSearchLink
				header="No pricing details yet"
				text={
					category === 'WPH'
						? 'Looking for the perfect match?'
						: 'We’re here to help! Find other vendors that suit your budget.'
				}
				linkText="Continue browsing vendors"
				storefrontSection="Packages"
				inline={category === 'WPH'}
			/>
		);
	}

	return (
		<div className={Styles.noPricingDetailsWrapper}>
			<div className={Styles.noPricingDetails}>
				<Body1 bold>No pricing details yet</Body1>
				<Body1>Reach out to get a customized quote from this vendor.</Body1>
			</div>
			<PricingCta sourceContent="Packages" />
		</div>
	);
};

const PricingDetails = () => {
	const isLiteStorefront = useLiteStorefront();
	const categoryCode = useAppSelector(
		(state) => state.vendor.vendor?.categoryCode as CategoryCode,
	);
	const singularTerm = CATEGORY_SINGULAR_ABBREVIATED_TERMS[categoryCode];
	const pricingDetailsText = `Starting prices may not include all fees a vendor may charge. Contact the ${singularTerm} for customized services and quotes.`;
	const isCaterer = categoryCode === 'CAT';
	const showPdf = isLiteStorefront || isCaterer;
	return (
		<div className={Styles.pdfCtaWrapper}>
			{showPdf && (
				<PricingPdf text={PDF_TEXT}>
					<div className={Styles.divider} />
				</PricingPdf>
			)}
			<div className={Styles.iconWrapper}>
				<Icon size="sm" name="info" aria-label="Information" />
				{pricingDetailsText}
			</div>
			<PricingCta sourceContent="Packages" isPackagePricing />
		</div>
	);
};

const CouplesUsuallySpend = ({ hasPackages }: { hasPackages: boolean }) => {
	const couplesUsuallySpend = useAtomValue(detailedPricingUsuallySpendAtom);

	if (!hasPackages) {
		return (
			<CouplesUsuallySpendEmptyDetails
				couplesUsuallySpend={couplesUsuallySpend}
			/>
		);
	}

	return (
		<ul className={Styles.usuallySpendDisclaimerList}>
			<li>Starting prices may not include all fees a vendor may charge.</li>
			{couplesUsuallySpend && (
				<li>Couples usually spend {couplesUsuallySpend}</li>
			)}
		</ul>
	);
};

interface AdditionalPricingDetailsProps {
	shouldShowCUS: boolean;
	hasPricingDetails: boolean;
}

const AdditionalPricingDetails = ({
	shouldShowCUS,
	hasPricingDetails,
}: AdditionalPricingDetailsProps) => {
	const couplesUsuallySpend = useAtomValue(detailedPricingUsuallySpendAtom);

	if (shouldShowCUS) {
		return (
			<CUSSecondaryCta
				couplesUsuallySpend={couplesUsuallySpend}
				isPackagePricing
			/>
		);
	}

	return hasPricingDetails ? <PricingDetails /> : <NoPricingDetails />;
};

export const PackagePricing: FC<NavItemProps> = () => {
	const isLiteStorefront = useLiteStorefront();
	const shouldShowCUS = useStorefrontCouplesUsuallySpend();
	const packages = useAtomValue(packagesAtom);
	const pdfUrl = usePdfUrl();
	const isCaterer = useAppSelector(
		(state) => state.vendor.vendor?.categoryCode === 'CAT',
	);
	const hasPricingDetails = Boolean(
		!isLiteStorefront
			? packages.length || (isCaterer && pdfUrl?.length)
			: packages.length || pdfUrl?.length,
	);
	const heading = isCaterer ? 'Pricing details' : 'Prices & packages';
	const milestone = isCaterer ? 'Pricing' : 'Detailed Pricing Packages';
	const hideDetailedPricing = useAtomValue(hideDetailedPricingAtom);

	const trackingRef = useTrackStorefrontImpression(
		!isLiteStorefront ? milestone : 'Packages',
	);
	const isInEstimatedPricing = useEstimatedPricing();
	if (hideDetailedPricing) {
		return null;
	}

	return (
		<div className={Styles.pricesAndPackages}>
			<H3 ref={trackingRef}>{heading}</H3>
			{!isCaterer && !isLiteStorefront && <Packages />}
			{!packages.length && isInEstimatedPricing && !isLiteStorefront && (
				<EstimatedUsuallySpend />
			)}
			{shouldShowCUS && (
				<>
					<CouplesUsuallySpend hasPackages={Boolean(packages.length)} />
					{isLiteStorefront && (
						<RelatedSearches headline="Explore other vendors that suit your budget" />
					)}
					{!isLiteStorefront && isCaterer && <PricingPdf text={PDF_TEXT} />}
				</>
			)}
			{!isLiteStorefront && (
				<AdditionalPricingDetails
					shouldShowCUS={shouldShowCUS}
					hasPricingDetails={hasPricingDetails}
				/>
			)}
		</div>
	);
};
